import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, RotateCcw, RefreshCw } from "lucide-react";

const WEIGHT_LABELS = { prezzo: "Prezzo", margine: "Margine teorico", occupazione: "Occupazione", liberazione: "Facilità liberazione", regolarita: "Regolarità urbanistica", liquidabilita: "Liquidabilità", comune: "Comune", domanda: "Domanda immobiliare" };
const CRIT_LABELS = { roi_min: "ROI minimo (%)", sconto_min: "Sconto minimo (%)", abusi_max_pct: "Abusi max (% valore)", creditori_max: "N. creditori max (S&S)" };
const SOGLIE_LABELS = { score_alta: "Score alto (notifica)", roi_alto: "ROI alto (notifica %)" };
const ASSUNZIONI_LABELS = { aliquota_imposte: "Aliquota imposte", notaio_pct: "Notaio (% prezzo)", costo_mq_ristrutturazione: "Costo ristrutturazione €/mq", mesi_holding: "Mesi holding", holding_mensile_pct: "Holding mensile (% prezzo)", equity_pct: "Equity (% investimento)" };

function Card({ title, subtitle, children, testid }) {
  return (
    <div className="bg-[#122A3F] border border-white/10 rounded-sm p-5" data-testid={testid}>
      <h2 className="text-sm font-medium text-zinc-200 uppercase tracking-wider">{title}</h2>
      {subtitle && <p className="text-xs text-zinc-500 mt-1 mb-4">{subtitle}</p>}
      <div className="space-y-3 mt-4">{children}</div>
    </div>
  );
}

function NumRow({ label, value, onChange, step = 1, testid }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-sm text-zinc-400">{label}</span>
      <input
        type="number"
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        data-testid={testid}
        className="w-28 bg-[#0A1826] border border-white/10 rounded-sm px-3 py-1.5 text-sm text-white font-mono text-right focus:outline-none focus:ring-1 focus:ring-[#EA7B2B]"
      />
    </div>
  );
}

export default function Settings() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [recomputing, setRecomputing] = useState(false);

  useEffect(() => { api.get("/config").then((r) => setCfg(r.data)).catch(() => {}); }, []);

  if (!cfg)
    return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 text-[#EA7B2B] animate-spin" /></div>;

  const totalWeight = Object.values(cfg.scoring_weights).reduce((a, b) => a + Number(b), 0);
  const setField = (group, key, val) => setCfg((c) => ({ ...c, [group]: { ...c[group], [key]: val } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/config", { scoring_weights: cfg.scoring_weights, criteri: cfg.criteri, soglie_notifica: cfg.soglie_notifica, assunzioni: cfg.assunzioni });
      toast.success("Configurazione salvata");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    const { data } = await api.post("/config/reset");
    setCfg(data);
    toast.success("Configurazione ripristinata ai valori di default");
  };

  const recompute = async () => {
    setRecomputing(true);
    try {
      const { data } = await api.post("/recompute");
      toast.success(`${data.ricalcolate} operazioni ricalcolate con i nuovi parametri`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setRecomputing(false); }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-light text-white">Configurazione motore</h1>
          <p className="text-sm text-zinc-500 mt-1">Pesi di scoring, criteri di qualificazione, soglie e assunzioni di costo</p>
        </div>
        <div className="flex gap-3">
          <button onClick={reset} data-testid="reset-config-btn" className="flex items-center gap-2 text-sm text-zinc-400 border border-white/10 px-4 py-2.5 rounded-sm hover:bg-white/5"><RotateCcw className="w-4 h-4" /> Default</button>
          <button onClick={recompute} disabled={recomputing} data-testid="recompute-btn" className="flex items-center gap-2 text-sm text-white border border-[#EA7B2B]/40 px-4 py-2.5 rounded-sm hover:bg-[#EA7B2B]/10 disabled:opacity-60">{recomputing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Ricalcola tutto</button>
          <button onClick={save} disabled={saving} data-testid="save-config-btn" className="flex items-center gap-2 text-sm font-medium bg-[#EA7B2B] text-black px-5 py-2.5 rounded-sm hover:bg-[#F59E4A] disabled:opacity-60">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva</button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Pesi Scoring Engine" subtitle={`Totale: ${totalWeight}/100 ${totalWeight !== 100 ? "⚠ dovrebbe essere 100" : "✓"}`} testid="config-weights">
          {Object.entries(cfg.scoring_weights).map(([k, v]) => (
            <NumRow key={k} label={WEIGHT_LABELS[k] || k} value={v} onChange={(val) => setField("scoring_weights", k, val)} testid={`weight-${k}`} />
          ))}
        </Card>

        <Card title="Criteri di qualificazione" subtitle="Criteri bloccanti per la qualificazione delle operazioni" testid="config-criteri">
          {Object.entries(cfg.criteri).map(([k, v]) => (
            <NumRow key={k} label={CRIT_LABELS[k] || k} value={v} step={0.5} onChange={(val) => setField("criteri", k, val)} testid={`crit-${k}`} />
          ))}
        </Card>

        <Card title="Soglie di notifica" subtitle="Eventi che generano alert automatici" testid="config-soglie">
          {Object.entries(cfg.soglie_notifica).map(([k, v]) => (
            <NumRow key={k} label={SOGLIE_LABELS[k] || k} value={v} onChange={(val) => setField("soglie_notifica", k, val)} testid={`soglia-${k}`} />
          ))}
        </Card>

        <Card title="Assunzioni di costo" subtitle="Parametri economici del financial engine" testid="config-assunzioni">
          {Object.entries(cfg.assunzioni).map(([k, v]) => (
            <NumRow key={k} label={ASSUNZIONI_LABELS[k] || k} value={v} step={0.005} onChange={(val) => setField("assunzioni", k, val)} testid={`ass-${k}`} />
          ))}
        </Card>
      </div>
    </div>
  );
}
